/**
 * Reusable Asset Picker Dialog Component
 * Allows selecting images from CMS assets or uploading new ones to Cloudinary
 */
import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Image, Loader2, Upload, Search, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

export const AssetPickerDialog = ({
  open,
  onOpenChange,
  onSelect,
  title = "Select Image from Assets",
  description = "Choose an image from your CMS assets library.",
  filterCategory = null, // Optional: filter by category (e.g., 'team', 'testimonials')
  allowUpload = true,
  uploadCategory = 'general',
  uploadAssetKeyPrefix = 'asset'
}) => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);
  
  const token = localStorage.getItem('cms_token');

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/assets/?active_only=true`;
      if (filterCategory) {
        url += `&category=${filterCategory}`;
      }
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        // Filter to only image-type assets and deduplicate by URL
        const seen = new Set();
        const images = (data.assets || []).filter(a => {
          const isImage = a.type === 'image' || 
            a.type === 'logo' ||
            a.url?.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i);
          const url = a.url || '';
          if (!isImage || !url || seen.has(url)) return false;
          seen.add(url);
          return true;
        });
        setAssets(images);
      }
    } catch (error) {
      console.error('Error fetching assets:', error);
    } finally {
      setLoading(false);
    }
  }, [filterCategory]);

  useEffect(() => {
    if (open) {
      fetchAssets();
    }
  }, [open, fetchAssets]);

  const handleUpload = async (file) => {
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name.replace(/\.[^/.]+$/, ''));
    formData.append('asset_key', `${uploadAssetKeyPrefix}_${Date.now()}`);
    formData.append('type', 'image');
    formData.append('category', uploadCategory);
    formData.append('description', `Uploaded via Asset Picker`);
    
    try {
      const res = await fetch(`${API_URL}/api/assets/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success('Image uploaded successfully!');
        onSelect(data.url);
        onOpenChange(false);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to upload image');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  // Filter assets by search term
  const filteredAssets = assets.filter(a => 
    !searchTerm || 
    a.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.asset_key?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[750px] max-h-[85vh]" aria-describedby="asset-picker-desc">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Image className="w-5 h-5 text-blue-600" />
            {title}
          </DialogTitle>
        </DialogHeader>
        
        <p id="asset-picker-desc" className="text-sm text-slate-500 -mt-2">
          {description}
        </p>
        
        {/* Search and Upload Row */}
        <div className="flex gap-2 items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search assets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {allowUpload && (
            <label className="cursor-pointer">
              <Button
                type="button"
                variant="outline"
                disabled={uploading}
                onClick={() => document.getElementById('asset-picker-upload').click()}
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Upload New
              </Button>
              <input
                id="asset-picker-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleUpload(file);
                  e.target.value = '';
                }}
              />
            </label>
          )}
        </div>
        
        <div className="py-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : filteredAssets.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Image className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>{searchTerm ? 'No matching images found' : 'No images available'}</p>
              {allowUpload && (
                <p className="text-sm mt-1">Upload a new image to get started</p>
              )}
            </div>
          ) : (
            <div 
              className="max-h-[450px] overflow-y-auto pr-2"
              style={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '12px',
                padding: '4px'
              }}
            >
              {filteredAssets.map((asset) => (
                <button
                  key={asset.asset_id || asset.asset_key || Math.random()}
                  type="button"
                  onClick={() => {
                    onSelect(asset.url);
                    onOpenChange(false);
                  }}
                  className="group relative rounded-lg overflow-hidden border-2 border-slate-200 hover:border-blue-500 hover:shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 bg-slate-100"
                  style={{
                    aspectRatio: '1/1',
                    minHeight: '120px'
                  }}
                  data-testid={`asset-option-${asset.asset_key}`}
                >
                  <img 
                    src={asset.url} 
                    alt={asset.name || 'Asset'}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
                    <span className="text-white text-sm font-medium px-3 py-1.5 bg-blue-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
                      Select
                    </span>
                  </div>
                  {/* Name label */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-2 pt-6">
                    <p className="text-white text-xs font-medium truncate drop-shadow-sm">
                      {asset.name || 'Unnamed'}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        
        <DialogFooter>
          <p className="text-xs text-slate-500 mr-auto">
            {filteredAssets.length} image{filteredAssets.length !== 1 ? 's' : ''} available
          </p>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Reusable Image Field Component with Asset Picker integration
 * Use this for any image URL field in CMS forms
 */
export const ImageFieldWithPicker = ({
  value,
  onChange,
  onClear,
  label = "Image",
  placeholder = "Select or paste image URL",
  previewSize = "w-20 h-20",
  previewShape = "rounded-lg", // or "rounded-full" for avatars
  filterCategory = null,
  uploadCategory = 'general',
  uploadAssetKeyPrefix = 'image',
  showPreview = true,
  className = ""
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const token = localStorage.getItem('cms_token');
  const [uploading, setUploading] = useState(false);

  const handleDirectUpload = async (file) => {
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name.replace(/\.[^/.]+$/, ''));
    formData.append('asset_key', `${uploadAssetKeyPrefix}_${Date.now()}`);
    formData.append('type', 'image');
    formData.append('category', uploadCategory);
    formData.append('description', `Uploaded via Image Field`);
    
    try {
      const res = await fetch(`${window.location.origin}/api/assets/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        onChange(data.url);
        toast.success('Image uploaded!');
      } else {
        toast.error('Upload failed');
      }
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={className}>
      {label && <label className="text-sm font-medium mb-2 block">{label}</label>}
      
      <div className="flex items-start gap-3">
        {/* Preview */}
        {showPreview && (
          <div className="flex-shrink-0 relative group">
            {value ? (
              <div className="relative">
                <img 
                  src={value} 
                  alt="Preview"
                  className={`${previewSize} ${previewShape} object-cover border-2 border-slate-200`}
                  onError={(e) => { e.target.src = 'https://via.placeholder.com/100?text=Error'; }}
                />
                {onClear && (
                  <button
                    type="button"
                    onClick={onClear}
                    className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Remove image"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            ) : (
              <div className={`${previewSize} ${previewShape} bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400`}>
                <Image className="w-6 h-6" />
              </div>
            )}
          </div>
        )}
        
        {/* Controls */}
        <div className="flex-1 space-y-2">
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setPickerOpen(true)}
            >
              <Image className="w-4 h-4 mr-2" />
              Browse Assets
            </Button>
            
            <label className="cursor-pointer">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={uploading}
                onClick={() => document.getElementById(`upload-${uploadAssetKeyPrefix}`).click()}
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Upload New
              </Button>
              <input
                id={`upload-${uploadAssetKeyPrefix}`}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleDirectUpload(file);
                  e.target.value = '';
                }}
              />
            </label>
          </div>
          
          <Input
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="text-sm"
          />
        </div>
      </div>
      
      <AssetPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onSelect={onChange}
        filterCategory={filterCategory}
        uploadCategory={uploadCategory}
        uploadAssetKeyPrefix={uploadAssetKeyPrefix}
      />
    </div>
  );
};

export default AssetPickerDialog;
